import React, { useState, useEffect } from 'react';
import Table from '../../../Table/Table';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import axios from 'axios';

const MaterialReturn = () => {
  const [selectedType, setSelectedType] = useState('Pallet');
  const [inputValue, setInputValue] = useState('');
  const [contractor, setContractor] = useState('');
  const [items, setItems] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('srNo');
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogAction, setDialogAction] = useState('');
  const [contractors, setContractors] = useState([]);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const currentDate = new Date().toISOString().split('T')[0];

  // Fetch contractors on mount
  useEffect(() => {
    const fetchContractors = async () => {
      try {
        const projectId = localStorage.getItem('selectedProject');
        if (!projectId) {
          setError('Project ID not found in local storage');
          setContractor('');
          return;
        }

        setLoading(true);
        const response = await axios.post(`${baseURL}get_contractor_names`, {
          projectId,
        });

        const contractorNames = Array.isArray(response.data.data)
          ? response.data.data.map(item => typeof item === 'string' ? item : item.name).filter(Boolean)
          : [];
        setContractors(contractorNames);
        if (contractorNames.length === 0) {
          setError('No contractors found for this project');
          setContractor('');
        } else {
          setError(null);
        }
      } catch (err) {
        console.error('Error fetching contractors:', err);
        setError('Failed to fetch contractors');
        setContractor('');
      } finally {
        setLoading(false);
      }
    };
    fetchContractors();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim() && contractor) {
      const newItem = {
        type: selectedType,
        value: inputValue.trim(),
        srNo: items.length + 1,
      };
      setItems([...items, newItem]);
      setInputValue('');
      setError(null);
      setSuccessMessage(null);
    }
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleOpenDialog = (action) => {
    const filtered = items.filter(item => item.type === selectedType);
    if (!filtered.length) {
      setError(`No ${selectedType.toLowerCase()}s to ${action.toLowerCase()}`);
      setSuccessMessage(null);
      return;
    }
    setDialogAction(action);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogAction('');
  };

  const handleRemoveItem = (srNo) => {
    setItems(prev => {
      const filtered = prev.filter(item => item.srNo !== srNo);
      // Re-index srNo
      return filtered.map((item, idx) => ({ ...item, srNo: idx + 1 }));
    });
  };

  const handleConfirmAction = async () => {
    try {
      const projectId = localStorage.getItem('selectedProject');
      if (!projectId) {
        setError('Project ID not found in local storage');
        setSuccessMessage(null);
        return;
      }

      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      const payload = {
        projectId,
        contractor,
        pallets: selectedType === 'Pallet' ? items.filter(item => item.type === 'Pallet').map(item => item.value) : [],
        modules: selectedType === 'Module' ? items.filter(item => item.type === 'Module').map(item => item.value) : [],
        action: dialogAction.toLowerCase(),
        date: currentDate,
        module_or_pallet: selectedType.toLowerCase(),
      };

      const response = await axios.post(`${baseURL}assign_return_pallets_modules`, payload);

      if (response.data.status === 'success') {
        setSuccessMessage(response.data.message || `Successfully ${dialogAction.toLowerCase()}ed ${selectedType.toLowerCase()}s`);
        setItems(items.filter(item => item.type !== selectedType));
        setError(null);
      } else {
        setError(response.data.message || response.data.error || `Failed to ${dialogAction.toLowerCase()} ${selectedType.toLowerCase()}s`);
        setSuccessMessage(null);
      }
    } catch (err) {
      console.error(`Error ${dialogAction.toLowerCase()}ing ${selectedType.toLowerCase()}s:`, err);
      setError(err.response?.data?.error || `Failed to ${dialogAction.toLowerCase()} ${selectedType.toLowerCase()}s`);
      setSuccessMessage(null);
    } finally {
      setLoading(false);
      handleCloseDialog();
    }
  };

  // Table Headers with Remove Column
  const tableHeaders = [
    { dbParam: 'srNo', displayName: 'Sr. No' },
    { dbParam: 'value', displayName: `${selectedType} No` },
    { dbParam: 'remove', displayName: 'Remove', sortable: false },
  ];

  const sortedItems = [...items]
    .filter((item) => item.type === selectedType)
    .sort((a, b) => {
      if (orderBy === 'srNo') {
        return order === 'asc' ? a.srNo - b.srNo : b.srNo - a.srNo;
      }
      return order === 'asc'
        ? a.value.localeCompare(b.value)
        : b.value.localeCompare(a.value);
    });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-50 p-6">
      <div className="max-h-screen mx-auto bg-white rounded-2xl shadow-xl p-8 transform transition-all hover:shadow-2xl">
        <h1 className="text-3xl font-bold text-center text-gray-600 mb-8">
          Material Assign and Return
        </h1>

        {loading && (
          <Typography align="center" color="textSecondary" style={{ marginBottom: '20px' }}>
            Loading...
          </Typography>
        )}
        {successMessage && (
          <Typography align="center" style={{ color: 'green', marginBottom: '20px' }}>
            {successMessage}
          </Typography>
        )}
        {error && (
          <Typography color="error" align="center" style={{ marginBottom: '20px' }}>
            {error}
          </Typography>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
            <input
              type="date"
              value={currentDate}
              disabled
              className="w-full py-2 px-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contractor</label>
            <select
              value={contractor}
              onChange={(e) => setContractor(e.target.value)}
              className="w-full py-3 px-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition"
              disabled={loading || contractors.length === 0}
            >
              <option value="" disabled>Select Contractor</option>
              {contractors.map((c, index) => (
                <option key={index} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center space-x-4 mb-8">
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="py-3 px-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition w-1/4"
            disabled={loading}
          >
            <option value="Pallet">Pallet</option>
            <option value="Module">Module Sr No</option>
          </select>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={`Enter ${selectedType} Number`}
            className="flex-1 py-2 px-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition"
            disabled={loading}
          />
          <button
            onClick={handleSubmit}
            disabled={!inputValue.trim() || !contractor || loading}
            className="py-2 px-6 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Submit
          </button>
        </div>

        {/* Table with Remove Button */}
        <Table
          tableHeaders={tableHeaders}
          tableData={sortedItems}
          order={order}
          orderBy={orderBy}
          handleSort={handleSort}
          renderCell={(header, row) => {
            if (header.dbParam === 'remove') {
              return (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveItem(row.srNo);
                  }}
                  className="text-red-600 hover:text-red-800 font-bold text-xl text-center transition-transform hover:scale-110"
                  title={`Remove ${row.value}`}
                  disabled={loading}
                >
                  x
                </button>
              );
            }
            return null;
          }}
        />

        <div className="flex justify-end space-x-4 mt-6">
          <button
            onClick={() => handleOpenDialog('Assign')}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-green-500 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
            disabled={loading || !items.filter(i => i.type === selectedType).length}
          >
            Assign
          </button>
          <button
            onClick={() => handleOpenDialog('Return')}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-red-500 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
            disabled={loading || !items.filter(i => i.type === selectedType).length}
          >
            Return
          </button>
        </div>

        {/* Confirmation Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog}>
          <DialogTitle>Confirm {dialogAction}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to {dialogAction.toLowerCase()} the selected {selectedType.toLowerCase()}s?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="secondary" disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleConfirmAction} color="primary" variant="contained" disabled={loading}>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default MaterialReturn;